import { StreamLanguage } from '@codemirror/language'
import type { Extension } from '@codemirror/state'
import { DEFAULT_LANGUAGE, LANGUAGE_IDS, type LanguageId } from '../shared/types'

type Loader = () => Promise<Extension>

/** Explicit static imports so Vite can code-split each grammar into its own chunk. */
const LOADERS: Record<LanguageId, Loader | null> = {
  text: null,
  shell: async () => StreamLanguage.define((await import('@codemirror/legacy-modes/mode/shell')).shell),
  powershell: async () =>
    StreamLanguage.define((await import('@codemirror/legacy-modes/mode/powershell')).powerShell),
  yaml: async () => (await import('@codemirror/lang-yaml')).yaml(),
  json: async () => (await import('@codemirror/lang-json')).json(),
  dockerfile: async () =>
    StreamLanguage.define((await import('@codemirror/legacy-modes/mode/dockerfile')).dockerFile),
  nginx: async () => StreamLanguage.define((await import('@codemirror/legacy-modes/mode/nginx')).nginx),
  // Ours rather than a legacy mode — see terraform.ts for why.
  terraform: async () => (await import('./terraform')).terraform,
  toml: async () => StreamLanguage.define((await import('@codemirror/legacy-modes/mode/toml')).toml),
  properties: async () =>
    StreamLanguage.define((await import('@codemirror/legacy-modes/mode/properties')).properties),
  javascript: async () => (await import('@codemirror/lang-javascript')).javascript(),
  typescript: async () => (await import('@codemirror/lang-javascript')).javascript({ typescript: true }),
  python: async () => (await import('@codemirror/lang-python')).python(),
  go: async () => (await import('@codemirror/lang-go')).go(),
  rust: async () => (await import('@codemirror/lang-rust')).rust(),
  sql: async () => (await import('@codemirror/lang-sql')).sql(),
  markdown: async () => (await import('@codemirror/lang-markdown')).markdown(),
  html: async () => (await import('@codemirror/lang-html')).html(),
  css: async () => (await import('@codemirror/lang-css')).css(),
  xml: async () => StreamLanguage.define((await import('@codemirror/legacy-modes/mode/xml')).xml),
  diff: async () => StreamLanguage.define((await import('@codemirror/legacy-modes/mode/diff')).diff),
}

const LABELS: Record<LanguageId, string> = {
  text: 'plain text',
  shell: 'shell',
  powershell: 'PowerShell',
  yaml: 'YAML',
  json: 'JSON',
  dockerfile: 'Dockerfile',
  nginx: 'nginx',
  terraform: 'Terraform',
  toml: 'TOML',
  properties: 'env / properties',
  javascript: 'JavaScript',
  typescript: 'TypeScript',
  python: 'Python',
  go: 'Go',
  rust: 'Rust',
  sql: 'SQL',
  markdown: 'Markdown',
  html: 'HTML',
  css: 'CSS',
  xml: 'XML',
  diff: 'diff',
}

export const LANGUAGES = LANGUAGE_IDS.map((id) => ({ id, label: LABELS[id] }))

export function languageLabel(id: LanguageId): string {
  return LABELS[id]
}

export async function languageExtension(id: LanguageId): Promise<Extension> {
  const loader = LOADERS[id]
  if (!loader) return []
  try {
    return await loader()
  } catch {
    // A grammar chunk failing to load shouldn't take the page down with it.
    return []
  }
}

/** Cheap heuristics, ordered most-specific first. Good enough to save a click. */
export function detectLanguage(text: string): LanguageId {
  const sample = text.slice(0, 4096)
  const head = sample.trimStart()
  if (!head) return DEFAULT_LANGUAGE

  if (/^#!.*\b(bash|sh|zsh|dash|ksh|ash)\b/.test(head)) return 'shell'
  if (/^#!.*\bpython[\d.]*\b/.test(head)) return 'python'
  if (/^(diff --git |index [0-9a-f]{7,}|@@ -\d)/m.test(sample)) return 'diff'

  if (/^\s*(FROM|ARG)\s+\S+/m.test(sample) && /^\s*(RUN|COPY|ADD|CMD|ENTRYPOINT|WORKDIR|EXPOSE|ENV)\s/m.test(sample)) {
    return 'dockerfile'
  }

  if (/^[{[]/.test(head) && isJson(head)) return 'json'
  if (/^<\?xml\b/.test(head)) return 'xml'
  if (/^<!DOCTYPE html|^<html\b/i.test(head)) return 'html'

  if (/^(apiVersion|kind|metadata|spec):\s/m.test(sample)) return 'yaml'
  if (/^---\s*$/m.test(sample) && /^[\w.-]+:(\s|$)/m.test(sample)) return 'yaml'

  if (/^#!.*\bpwsh\b/.test(head)) return 'powershell'
  if (/\[CmdletBinding\(|\$PSScriptRoot\b|\$PSVersionTable\b|\$ErrorActionPreference\b/i.test(sample)) {
    return 'powershell'
  }
  if (
    /^\s*\|?\s*(Write-Host|Write-Output|Write-Error|Get-ChildItem|Get-Content|Set-Content|Get-Item|New-Item|Remove-Item|New-Object|Invoke-RestMethod|Invoke-WebRequest|Invoke-Command|Import-Module|Test-Path|Join-Path|Select-Object|Where-Object|ForEach-Object|Sort-Object|Measure-Object|Start-Process|Stop-Process|Get-Service|Get-Process|Set-ExecutionPolicy|ConvertTo-Json|ConvertFrom-Json)\b/m.test(
      sample,
    )
  ) {
    return 'powershell'
  }
  if (/^\s*param\s*\(/im.test(sample) && /\$[A-Za-z_]/.test(sample)) return 'powershell'

  // Before toml, which also matches on bare `key = value` lines. The block
  // header is what separates them: a quoted label list ending in a brace.
  if (
    /^\s*(?:resource|data|provider|variable|output|module|locals|terraform)\s*(?:"[^"]*"\s*)*\{/m.test(
      sample,
    )
  ) {
    return 'terraform'
  }

  if (/^\s*(http|server|location|upstream|events)\s*\{/m.test(sample)) return 'nginx'
  if (/^\s*\[[\w.".-]+\]\s*$/m.test(sample) && /^\s*[\w.-]+\s*=/m.test(sample)) return 'toml'

  if (/^\s*(SELECT|INSERT INTO|UPDATE|DELETE FROM|CREATE (TABLE|INDEX|VIEW)|ALTER TABLE|WITH)\b/im.test(head)) {
    return 'sql'
  }

  if (/^\s*(def|class)\s+\w+|^\s*from\s+[\w.]+\s+import\b|^\s*import\s+\w+\s*$/m.test(sample)) return 'python'
  if (/^\s*package\s+\w+/m.test(sample) && /\bfunc\s/.test(sample)) return 'go'
  if (/\bfn\s+\w+\s*[(<]/.test(sample) && /(let\s+mut\b|::|impl\s|->\s*\w)/.test(sample)) return 'rust'

  if (/^\s*(interface|type)\s+\w+\s*[={<]/m.test(sample) || /:\s*(string|number|boolean)\b/.test(sample)) {
    return 'typescript'
  }
  if (/\b(const|let|var|function|=>|require\(|module\.exports|console\.log)\b/.test(sample)) return 'javascript'

  if (/^#{1,6}\s+\S/m.test(sample) || /^```/m.test(sample) || /^\s*[-*]\s+\S/m.test(sample)) return 'markdown'
  if (/^[A-Z_][A-Z0-9_]*=/m.test(sample)) return 'properties'
  if (/^\s*[.#@\w][\w\s.#:>,[\]="'()-]*\{[^}]*:[^}]*;/m.test(sample)) return 'css'
  if (/^\s*[\w.-]+:\s+\S/m.test(sample)) return 'yaml'

  return DEFAULT_LANGUAGE
}

function isJson(text: string): boolean {
  try {
    JSON.parse(text)
    return true
  } catch {
    return false
  }
}
